import { Button, Form, FormControlProps, InputGroup } from "react-bootstrap";
import { Controller, useFormContext } from "react-hook-form";
import { BaseInputProps, ErrorMessage } from ".";
import { usePermissions } from "../../../contexts";
import { ChangeEvent, ReactNode, useCallback, useMemo, useState } from "react";
import {
    getDisplayedAccessor,
    getFormError,
    getFormattedPhoneNumber,
    getNumericPhoneNumber,
    formatSSN,
    maskSSN,
} from "../../../helper-functions";
import { fas, Icon } from "../../icons";
import { PatternFormat } from "react-number-format";

export interface InputProps extends FormControlProps, BaseInputProps {
    label?: string;
    inlineLabel?: boolean;
    placeholder?: string;
    minLength?: number;
    maxLength?: number;
    min?: number;
    max?: number;
    prepend?: ReactNode;
    append?: ReactNode;
    isCurrency?: boolean;
    mask?: string;
    inGroup?: boolean;
    rows?: number;
    isPhone?: boolean;
    isSSN?: boolean;
    required?: boolean;
    showMaskedSSN?: boolean;
    invalidText?: string;
}

export function Input({
    accessor,
    label,
    "aria-label": ariaLabel,
    inlineLabel,
    prepend,
    append,
    isCurrency,
    mask,
    inGroup,
    viewClaim,
    editClaim,
    readOnly,
    suppressMargin,
    className,
    title,
    isPhone,
    isSSN,
    showMaskedSSN,
    invalidText,
    ...props
}: Readonly<InputProps>) {
    const {
        register,
        formState: { errors },
        control,
    } = useFormContext();
    const { onChange } = register(accessor, {
        setValueAs: isPhone || isSSN ? (value: string) => getFormattedPhoneNumber(value) : undefined,
    });
    const { hasPermission } = usePermissions();
    const error = useMemo(() => getFormError(errors, accessor), [errors, accessor]);

    const isReadOnly = useMemo(
        () => (editClaim ? !hasPermission(editClaim) : readOnly),
        [readOnly, editClaim, hasPermission]
    );
    const [isFocused, setIsFocused] = useState(false);
    const [showSSN, setShowSSN] = useState(false);

    const format = useMemo(() => {
        if (mask) return mask;
        if (isPhone) return "(###) ###-####";
        return undefined;
    }, [mask, isPhone]);

    const hasMask = !!mask || isPhone;

    const handleChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>, registeredOnChange: any = onChange) => {
            if (isPhone || isSSN) {
                const value = getNumericPhoneNumber(e.target.value);
                registeredOnChange({
                    ...e,
                    target: {
                        ...e.target,
                        value,
                    },
                });
            } else if (props.type === "number") {
                const parsedValue = parseFloat(e.target.value);
                registeredOnChange({
                    ...e,
                    target: {
                        ...e.target,
                        value: isNaN(parsedValue) ? null : parsedValue,
                    },
                });
            } else {
                registeredOnChange(e);
            }
        },
        [isPhone, isSSN, onChange, props.type]
    );

    if (!!viewClaim && !hasPermission(viewClaim)) return null;

    const handleFocus = () => {
        setIsFocused(true);
    };

    const handleBlur = () => {
        setIsFocused(false);
    };
    const getValue = (value: string) => {
        if (!isSSN) {
            return value;
        } else {
            if (showMaskedSSN || showSSN) {
                return formatSSN(value);
            } else {
                return isFocused ? formatSSN(value) : maskSSN(value);
            }
        }
    };

    if (!inGroup)
        return (
            <Controller
                control={control}
                name={accessor}
                render={({ field: { onChange, onBlur, ...otherFields } }) => {
                    return (
                        <Form.Group
                            controlId={accessor}
                            className={`${!suppressMargin && "mb-2"} ${className}`}
                            style={props.style}
                        >
                            <div className={inlineLabel ? "d-flex align-items-center" : ""}>
                                {!!label && (
                                    <Form.Label style={{ whiteSpace: "nowrap" }} className={inlineLabel ? "me-2" : ""}>
                                        {label} {props.required && <span className="text-danger">*</span>}
                                    </Form.Label>
                                )}
                                <InputGroup>
                                    {(!!prepend || isCurrency) && <InputGroup.Text>{prepend ?? "$"}</InputGroup.Text>}
                                    <Form.Control
                                        as={hasMask ? PatternFormat : undefined}
                                        format={format}
                                        mask={hasMask ? "_" : undefined}
                                        readOnly={isReadOnly}
                                        aria-readonly={isReadOnly}
                                        aria-label={ariaLabel ?? label ?? accessor}
                                        onFocus={isSSN ? handleFocus : undefined}
                                        onBlur={isSSN ? handleBlur : undefined}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e, onChange)}
                                        title={title ?? getDisplayedAccessor(accessor)}
                                        {...otherFields}
                                        {...props}
                                        value={getValue(otherFields.value)}
                                    />
                                    {invalidText && (
                                        <Form.Control.Feedback type="invalid">{invalidText}</Form.Control.Feedback>
                                    )}
                                    {!!append && <InputGroup.Text>{append}</InputGroup.Text>}
                                    {isSSN && (
                                        <Button
                                            title={showSSN ? "Hide" : "Show"}
                                            onClick={() => setShowSSN((prev) => !prev)}
                                            variant="outline-secondary"
                                        >
                                            <Icon icon={showSSN ? fas.faEyeSlash : fas.faEye} />
                                        </Button>
                                    )}
                                </InputGroup>
                            </div>
                            <ErrorMessage error={error} />
                        </Form.Group>
                    );
                }}
            />
        );

    return (
        <Controller
            control={control}
            name={accessor}
            render={({ field: { onChange, onBlur, ...otherFields } }) => {
                return (
                    <>
                        <Form.Control
                            as={hasMask ? PatternFormat : undefined}
                            format={format}
                            mask={hasMask ? "_" : undefined}
                            readOnly={isReadOnly}
                            aria-readonly={isReadOnly}
                            aria-label={ariaLabel ?? label ?? accessor}
                            className={className}
                            onFocus={isSSN ? handleFocus : undefined}
                            onBlur={isSSN ? handleBlur : undefined}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e, onChange)}
                            title={title ?? getDisplayedAccessor(accessor)}
                            {...otherFields}
                            {...props}
                            value={getValue(otherFields.value)}
                        />
                        {invalidText && <Form.Control.Feedback type="invalid">{invalidText}</Form.Control.Feedback>}
                        <ErrorMessage error={error} />
                    </>
                );
            }}
        />
    );
}
